import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Clock, BarChart2, ExternalLink, Mic } from "lucide-react";
import { useSite } from "@/context/ThemeContext";
import { Reveal } from "@/components/Reveal";
import { getIcon } from "@/lib/icons";

export default function CourseCategory() {
  const { categoryId } = useParams();
  const { data } = useSite();
  const navigate = useNavigate();
  if (!data) return null;

  const cat = data.categories.find((c) => c.id === categoryId);
  const list = data.courses.filter((c) => c.category === categoryId);
  if (!cat) return (
    <div className="pt-40 text-center"><p className="text-t">Category not found.</p>
      <Link to="/courses" className="text-primary-c">← Back to courses</Link></div>
  );
  const Icon = getIcon(cat.icon);
  const isEnglish = cat.id === "english";

  return (
    <div className="relative z-10 pt-28">
      <section className="mx-auto max-w-7xl px-5 py-12">
        <button onClick={() => navigate("/courses")} className="inline-flex items-center gap-2 text-muted-c hover:text-primary-c transition-colors mb-8" data-testid="back-to-courses">
          <ArrowLeft size={16} /> All Categories
        </button>
        <Reveal>
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(var(--primary-rgb),0.12)" }}>
              <Icon className="text-primary-c" size={32} />
            </div>
            <div>
              <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-t">{cat.name}</h1>
              <p className="text-muted-c text-lg mt-3 max-w-2xl">{cat.description}</p>
            </div>
          </div>
        </Reveal>

        {isEnglish && (
          <Reveal delay={0.1}>
            <div className="glass rounded-3xl p-8 sm:p-10 mt-10" data-testid="speakease-callout">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-primary-c flex items-center justify-center glow-primary"><Mic className="text-on-primary" size={24} /></div>
                <h2 className="font-display font-bold text-2xl text-t">SpeakEase Integration</h2>
              </div>
              <p className="text-muted-c text-lg max-w-2xl">Practice your spoken English with SpeakEase, our AI-powered English learning companion.</p>
              <a href="https://speakease.in" target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2 px-6 py-3 mt-6" data-testid="try-speakease">
                Try SpeakEase <ExternalLink size={16} />
              </a>
            </div>
          </Reveal>
        )}

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 mt-10">
          {list.map((c, i) => {
            const CIcon = getIcon(c.icon);
            return (
              <Reveal key={c.id} delay={(i % 3) * 0.08}>
                <div className="glass card-hover rounded-2xl p-7 h-full flex flex-col" data-testid={`course-${c.id}`}>
                  {c.imageUrl ? (
                    <div className="w-full h-40 rounded-xl overflow-hidden mb-5 bg-surface">
                      <img src={c.imageUrl} alt={c.name} loading="lazy" className="w-full h-full object-cover object-top" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: "rgba(var(--primary-rgb),0.12)" }}>
                      <CIcon className="text-primary-c" size={24} />
                    </div>
                  )}
                  <h3 className="font-display font-bold text-xl text-t">{c.name}</h3>
                  <div className="flex gap-4 mt-3 text-xs text-muted-c">
                    <span className="inline-flex items-center gap-1.5"><Clock size={13} className="text-primary-c" /> {c.duration}</span>
                    <span className="inline-flex items-center gap-1.5"><BarChart2 size={13} className="text-primary-c" /> {c.level}</span>
                  </div>
                  <p className="text-muted-c text-sm mt-3 flex-1">{c.description}</p>
                  <button onClick={() => navigate(`/admission?course=${encodeURIComponent(c.name)}`)} className="btn-primary mt-5 py-2.5 text-sm" data-testid={`enquire-${c.id}`}>Enquire</button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}
